import type { Participant } from '@zoom/videosdk';

import { logger } from '../../../../datadog/logger';
import { noop, normalizeError } from '../../../../utils';
import type { RemoteParticipant } from '../../../core/room/participant/RemoteParticipant';
import { MediaKind } from '../../../core/room/participant/media/types';
import {
  EventContext,
  RemoteParticipantEvent,
  RemoteParticipantEventHandler,
  RemoteParticipantMediaEventMap,
} from '../../../core/room/participant/types';
import { ZoomEventBus } from '../../ZoomEventBus';
import { VideoClient } from '../../types';
import type { SupportedRemoteParticipantEvent, SupportedRemoteParticipantEventHandler } from './events';
import {
  createZoomHandlerFromEventMapping,
  getZoomEventMappings,
  isEventSupported,
  transformZoomResponseToDelegator,
} from './mapping';

export class ZoomRemoteParticipant implements RemoteParticipant {
  #participant: Participant;
  #videoClient: VideoClient;

  constructor(participant: Participant, videoClient: VideoClient) {
    this.#participant = participant;
    this.#videoClient = videoClient;
  }

  get isConnected() {
    return !this.#participant.isInFailover;
  }

  get sid(): string {
    return String(this.#participant.userId);
  }

  get identity(): string {
    return this.#participant.userIdentity!;
  }

  get displayName(): string {
    return this.#participant.displayName;
  }

  on<K extends RemoteParticipantEvent>(
    event: K,
    handler: RemoteParticipantEventHandler<K>,
    eventContext?: EventContext,
  ) {
    const mediaKind = eventContext?.mediaKind;

    if (!mediaKind) {
      return noop;
    }

    if (!isEventSupported(event)) {
      logger.warn(`[ZoomRemoteParticipant: on] Unsupported event: ${event}`);
      return noop;
    }

    const eventMappings = getZoomEventMappings(event, mediaKind);
    if (!Array.isArray(eventMappings)) {
      logger.warn(
        `[ZoomRemoteParticipant: on] No configuration found for event: ${event} with mediaKind: ${mediaKind}`,
      );
      return noop;
    }

    const eventBus = ZoomEventBus.getInstance();
    const supportedHandler = handler as SupportedRemoteParticipantEventHandler;

    const detachListeners = eventMappings.map((eventMapping) => {
      const zoomEventHandler = createZoomHandlerFromEventMapping<SupportedRemoteParticipantEvent>({
        eventMapping,
        videoClient: this.#videoClient,
        participant: this.#participant,
        handler: supportedHandler,
        mediaKind,
      });

      // register listeners
      eventBus.on(eventMapping.zoomEvent, zoomEventHandler);

      // return list of detach callbacks
      return function detachListener() {
        eventBus.off(eventMapping.zoomEvent, zoomEventHandler);
      };
    });

    return function off() {
      detachListeners.forEach((detachListener) => detachListener());
    };
  }

  async dispatchEvent<K extends keyof RemoteParticipantMediaEventMap>(
    event: K,
    handler: RemoteParticipantMediaEventMap[K],
    mediaKind: MediaKind,
  ) {
    if (event !== 'mediaSubscribed') {
      logger.warn(`[ZoomRemoteParticipant: dispatchEvent] Unsupported event: ${event}`);
      return;
    }

    try {
      const delegator = transformZoomResponseToDelegator(this.#videoClient, this.#participant, mediaKind);
      await handler(delegator);
    } catch (error) {
      logger.error('[ZoomRemoteParticipant: dispatchEvent] Failed to handle event', {}, normalizeError(error));
    }
  }

  detachAudio(_audioElement: HTMLAudioElement): void {
    // no-op
    return;
  }
}
